import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Wine, Moon, Zap, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function AlcoholFreeDayGuide() {
  const navigate = useNavigate();

  const benefits = [
    { icon: Moon, title: "Better Sleep", desc: "Alcohol disrupts REM sleep. Tonight you'll sleep more deeply." },
    { icon: Zap, title: "More Energy Tomorrow", desc: "No hangover means clearer thinking and more vitality." },
    { icon: Heart, title: "Liver Recovery", desc: "Your liver can process stored toxins without new ones coming in." }
  ];

  const alternatives = [
    "Sparkling water with lime or cucumber",
    "Herbal tea (chamomile, peppermint, ginger)",
    "Kombucha for that fizzy, complex taste",
    "A warm bath or shower",
    "A 10-minute walk outside",
    "Call or text a friend",
    "Your favorite show or book",
    "Gentle stretching or yoga"
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6">
          <ArrowLeft className="mr-2 w-4 h-4" /> Back
        </Button>

        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
            <Wine className="w-8 h-8 text-purple-600" />
          </div>
          <h1 className="text-4xl font-serif font-bold text-secondary-900 mb-2">Alcohol-Free Day</h1>
          <p className="text-lg text-secondary-600">Commit to zero alcohol and notice the benefits</p>
        </div>

        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">Today's Benefits</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {benefits.map((b, i) => (
              <div key={i} className="p-4 bg-purple-50 rounded-xl border border-purple-200 text-center">
                <b.icon className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                <h3 className="font-bold text-secondary-900 mb-1">{b.title}</h3>
                <p className="text-secondary-600 text-sm">{b.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 border-2 border-purple-200 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">When the Urge Arises</h2>
          <p className="text-secondary-700 mb-4">Pause and ask yourself: "What am I really craving?"</p>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 bg-white rounded-xl">
              <h3 className="font-bold text-purple-700 mb-2">If you're craving relaxation...</h3>
              <p className="text-secondary-600 text-sm">Try a warm bath, gentle stretching, or deep breathing.</p>
            </div>
            <div className="p-4 bg-white rounded-xl">
              <h3 className="font-bold text-purple-700 mb-2">If you're craving connection...</h3>
              <p className="text-secondary-600 text-sm">Call a friend, text someone you love, or join an online community.</p>
            </div>
            <div className="p-4 bg-white rounded-xl">
              <h3 className="font-bold text-purple-700 mb-2">If you're craving reward...</h3>
              <p className="text-secondary-600 text-sm">Treat yourself to a fancy mocktail, favorite snack, or new show.</p>
            </div>
            <div className="p-4 bg-white rounded-xl">
              <h3 className="font-bold text-purple-700 mb-2">If you're craving escape...</h3>
              <p className="text-secondary-600 text-sm">Go for a walk, get lost in a book, or try a meditation app.</p>
            </div>
          </div>
        </section>

        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">Alternative Drinks & Activities</h2>
          <div className="grid md:grid-cols-2 gap-3">
            {alternatives.map((alt, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
                <span className="text-purple-500">✓</span>
                <span className="text-secondary-700">{alt}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-amber-50 rounded-2xl p-6 border border-amber-200">
          <h3 className="font-bold text-amber-800 mb-2">📝 Journal Prompt</h3>
          <p className="text-amber-900">
            "How did I feel when the urge to drink arose? What did I really need? How do I feel at the end of this alcohol-free day?"
          </p>
        </section>
      </main>
    </div>
  );
}

export default AlcoholFreeDayGuide;
